/* eslint-disable max-lines */
import {
  ChevronLeftIcon,
  PlusIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm
} from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import AsyncSelect from "react-select/async";
import FormWrapper from "../../../components/ui/FormWrapper";
import IconBtn from "../../../components/ui/IconBtn";
import Input from "../../../components/ui/input/Input";
import InputWrapper from "../../../components/ui/input/InputWrapper";
import useFetch from "../../../hooks/useFetch";
import {
  IBroadcastAudiencePreview,
  IBroadcastSaveRequest,
  IBroadcastUserSearchResult
} from "../../../interfaces/esim/BroadcastNotifications.types";
import {
  createBroadcast,
  getBroadcastAudiencePreview,
  getBroadcastById,
  searchBroadcastUsers,
  updateBroadcast
} from "../../../services/BroadcastNotification.service";
import {
  appLanguages,
  appRoutes,
  BroadcastStatus
} from "../../../utils/esim/broadcastStatus";
import BroadcastNotificationSchema from "../../../utils/validations/BroadcastNotification.schema";

interface TranslationShape {
  applicationLanguage: string;
  title: string;
  body: string;
}

interface FormShape {
  name: string;
  sendMode: "now" | "schedule";
  scheduledAt?: string;
  audienceMode: "all" | "users";
  targetUsers: IBroadcastUserSearchResult[];
  // Users to skip when audienceMode is "all". Never pre-filled on edit: the
  // backend does not store the list, it hands it to the send job.
  excludedUsers: IBroadcastUserSearchResult[];
  targetPlatform: string;
  targetLanguages: string[];
  minAppVersion?: string;
  routeChoice: string;
  customRoute?: string;
  defaultTitle: string;
  defaultBody: string;
  translations: TranslationShape[];
}

// Debounced so the user search hits the API once the admin pauses typing,
// not on every keystroke.
let userSearchTimer: ReturnType<typeof setTimeout> | undefined;
const loadUserOptions = (input: string) =>
  new Promise<IBroadcastUserSearchResult[]>((resolve) => {
    if (userSearchTimer) clearTimeout(userSearchTimer);
    if (input.trim().length < 2) {
      resolve([]);
      return;
    }
    userSearchTimer = setTimeout(() => {
      searchBroadcastUsers(input.trim())
        .then((res) => resolve(res.data || []))
        .catch(() => resolve([]));
    }, 300);
  });

const listCrumbs = ["Esim", "Njoftimet"];

const selectClassName = `text-sm leading-[18px] outline-none focus:ring-primary
  appearance-none w-full rounded-lg ring-1 ring-blue-stroke transition-all
  duration-300 hover:ring-gray-500 p-3 bg-white`;

const BroadcastForm: React.FC<{ editMode?: boolean }> = ({ editMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<IBroadcastAudiencePreview | null>(
    null
  );

  const methods = useForm<FormShape>({
    // Same yupResolver shim as EsimPartnerForm — yup's inferred shape doesn't
    // line up exactly with FormShape.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(BroadcastNotificationSchema) as any,
    defaultValues: {
      sendMode: "now",
      audienceMode: "all",
      targetUsers: [],
      excludedUsers: [],
      targetPlatform: "",
      targetLanguages: [],
      routeChoice: "",
      translations: []
    }
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitSuccessful, dirtyFields }
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "translations"
  });

  const sendMode = watch("sendMode");
  const audienceMode = watch("audienceMode");
  const targetUsers = watch("targetUsers");
  const excludedUsers = watch("excludedUsers");
  const targetPlatform = watch("targetPlatform");
  const targetLanguages = watch("targetLanguages");
  const minAppVersion = watch("minAppVersion");
  const routeChoice = watch("routeChoice");

  const { response } = useFetch(getBroadcastById, {
    initRequest: !!editMode,
    params: Number(id)
  });

  useEffect(() => {
    if (!editMode || !response?.data) return;
    const data = response.data;

    if (data.status !== BroadcastStatus.Scheduled) {
      toast.error("Ky njoftim nuk mund të modifikohet sepse dërgimi ka nisur");
      navigate(`/esim/broadcasts/${data.id}`, { state: listCrumbs });
      return;
    }

    const defaultMessage = data.messages.find((m) => m.isDefault);
    setValue("name", data.name);
    setValue("sendMode", data.scheduledAtUtc ? "schedule" : "now");
    if (data.scheduledAtUtc)
      setValue(
        "scheduledAt",
        dayjs.utc(data.scheduledAtUtc).tz().format("YYYY-MM-DDTHH:mm")
      );
    const hasUserTargets = (data.targetUserEmails || []).length > 0;
    setValue("audienceMode", hasUserTargets ? "users" : "all");
    setValue(
      "targetUsers",
      (data.targetUserEmails || []).map((email) => ({ email }))
    );
    setValue("targetPlatform", data.targetPlatform || "");
    setValue("targetLanguages", data.targetLanguages || []);
    setValue("minAppVersion", data.minAppVersion || "");
    if (data.targetRoute) {
      const known = appRoutes.some((r) => r.path === data.targetRoute);
      setValue("routeChoice", known ? data.targetRoute : "custom");
      if (!known) setValue("customRoute", data.targetRoute);
    } else {
      setValue("routeChoice", "");
    }
    setValue("defaultTitle", defaultMessage?.title || "");
    setValue("defaultBody", defaultMessage?.body || "");
    setValue(
      "translations",
      data.messages
        .filter((m) => !m.isDefault)
        .map((m) => ({
          applicationLanguage: m.applicationLanguage || "",
          title: m.title,
          body: m.body
        }))
    );
  }, [response, editMode, navigate, setValue]);

  useEffect(() => {
    let active = true;
    const userEmails =
      audienceMode === "users" ? (targetUsers || []).map((u) => u.email) : [];
    // The preview must apply the same exclusion the send will, or the number
    // shown here is not the number of devices that get the push.
    const excludedUserEmails =
      audienceMode === "all" ? (excludedUsers || []).map((u) => u.email) : [];

    if (audienceMode === "users" && userEmails.length === 0) {
      setPreview(null);
      return;
    }

    // Debounced: minAppVersion is free text, so without this every keystroke
    // would trigger a device-count query.
    const timer = setTimeout(() => {
      getBroadcastAudiencePreview(
        audienceMode === "users"
          ? { userEmails }
          : {
              platform: targetPlatform || undefined,
              languages: targetLanguages,
              minAppVersion: minAppVersion?.trim() || undefined,
              excludedUserEmails
            }
      )
        .then((res) => {
          if (active) setPreview(res.data);
        })
        .catch(() => {
          if (active) setPreview(null);
        });
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    audienceMode,
    JSON.stringify(targetUsers),
    JSON.stringify(excludedUsers),
    targetPlatform,
    JSON.stringify(targetLanguages),
    minAppVersion
  ]);

  const onSubmit: SubmitHandler<FormShape> = async (data) => {
    const specificUsers = data.audienceMode === "users";
    const targetRoute =
      data.routeChoice === "custom"
        ? data.customRoute?.trim() || null
        : data.routeChoice || null;

    const body: IBroadcastSaveRequest = {
      name: data.name,
      // dayjs.tz interprets the picked wall-clock time in the app's display
      // timezone (Europe/Belgrade) — the same zone used when re-displaying it —
      // so the stored instant doesn't depend on the admin's browser timezone.
      scheduledAtUtc:
        data.sendMode === "schedule" && data.scheduledAt
          ? dayjs.tz(data.scheduledAt).toISOString()
          : null,
      targetPlatform: specificUsers ? null : data.targetPlatform || null,
      targetLanguages: specificUsers ? [] : data.targetLanguages || [],
      minAppVersion: specificUsers ? null : data.minAppVersion?.trim() || null,
      targetUserEmails: specificUsers
        ? (data.targetUsers || []).map((u) => u.email)
        : [],
      // Only sent for "all": picking specific users and excluding some of them
      // is a contradiction the backend rejects, so the UI never offers it.
      excludedUserEmails: specificUsers
        ? []
        : (data.excludedUsers || []).map((u) => u.email),
      targetRoute,
      messages: [
        {
          isDefault: true,
          title: data.defaultTitle,
          body: data.defaultBody
        },
        ...data.translations.map((t) => ({
          applicationLanguage: t.applicationLanguage,
          isDefault: false,
          title: t.title,
          body: t.body
        }))
      ]
    };

    const request = editMode
      ? updateBroadcast(body, Number(id))
      : createBroadcast(body);

    await request
      .then(() => {
        toast.success(
          editMode
            ? "Njoftimi u modifikua me sukses"
            : data.sendMode === "schedule"
            ? "Njoftimi u planifikua me sukses"
            : "Njoftimi u vendos në radhë për dërgim"
        );
        setTimeout(
          () => navigate("/esim/broadcasts", { state: listCrumbs }),
          500
        );
      })
      .catch((error: unknown) => {
        const message =
          error && typeof error === "object" && "response" in error
            ? (error as { response?: { data?: { message?: string } } })
                .response?.data?.message
            : undefined;
        toast.error(message || "Ndodhi një gabim gjatë ruajtjes");
      });
  };

  const usedLanguages = watch("translations").map(
    (t) => t.applicationLanguage
  );

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/esim/broadcasts"
        state={listCrumbs}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        showPrompt={!isSubmitSuccessful && !!Object.keys(dirtyFields).length}
        onSubmit={handleSubmit(onSubmit)}
        title={editMode ? "Modifiko njoftimin" : "Krijo njoftim të ri"}
        contentClassName="max-w-2xl mx-auto"
      >
        <Input
          {...register("name")}
          label="Emri i njoftimit (vetëm për administratën, nuk u shfaqet përdoruesve)"
          error={errors.name?.message}
        />

        <div className="mb-4">
          <span className="text-sm block mb-2">Koha e dërgimit</span>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" value="now" {...register("sendMode")} />
              Dërgo menjëherë
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" value="schedule" {...register("sendMode")} />
              Planifiko për më vonë
            </label>
          </div>
        </div>

        {sendMode === "schedule" && (
          <Input
            {...register("scheduledAt")}
            type="datetime-local"
            label="Data dhe ora e dërgimit"
            error={errors.scheduledAt?.message}
          />
        )}

        <div className="mb-4 border-t pt-4">
          <span className="text-sm block mb-2 font-medium">Marrësit</span>
          <div className="flex items-center gap-6 mb-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" value="all" {...register("audienceMode")} />
              Të gjithë (me filtra)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" value="users" {...register("audienceMode")} />
              Përdorues të caktuar
            </label>
          </div>

          {audienceMode === "users" ? (
            <div className="mb-2">
              <Controller
                name="targetUsers"
                control={control}
                render={({ field: { value, onChange }, fieldState }) => (
                  <InputWrapper
                    label="Kërko përdoruesit me email"
                    error={fieldState.error?.message}
                  >
                    <AsyncSelect
                      isMulti
                      value={value}
                      onChange={(selected) => onChange(selected || [])}
                      loadOptions={loadUserOptions}
                      getOptionValue={(o: IBroadcastUserSearchResult) =>
                        o.email
                      }
                      getOptionLabel={(o: IBroadcastUserSearchResult) =>
                        o.email
                      }
                      placeholder="Shkruani të paktën 2 shkronja..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue.trim().length < 2
                          ? "Shkruani për të kërkuar"
                          : "Nuk u gjet asnjë përdorues"
                      }
                      loadingMessage={() => "Duke kërkuar..."}
                    />
                  </InputWrapper>
                )}
              />
              <p className="text-xs text-gray-500 mt-1">
                Njoftimi dërgohet në të gjitha pajisjet aktive të përdoruesve
                të zgjedhur.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="text-sm block mb-2" htmlFor="targetPlatform">
                  Platforma
                </label>
                <select
                  id="targetPlatform"
                  {...register("targetPlatform")}
                  className={selectClassName}
                >
                  <option value="">Të gjitha platformat</option>
                  <option value="ios">Vetëm iOS</option>
                  <option value="android">Vetëm Android</option>
                </select>
              </div>

              <div className="mb-4">
                <span className="text-sm block mb-2">
                  Gjuhët e synuara (asnjë e zgjedhur = të gjitha)
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {appLanguages.map((lang) => (
                    <label
                      key={lang.code}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        value={lang.code}
                        {...register("targetLanguages")}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      {lang.label}
                    </label>
                  ))}
                </div>
              </div>

              <Input
                {...register("minAppVersion")}
                label="Versioni minimal i aplikacionit (opsional, p.sh. 1.6.0)"
                error={errors.minAppVersion?.message}
              />

              <div className="mb-2">
                <Controller
                  name="excludedUsers"
                  control={control}
                  render={({ field: { value, onChange }, fieldState }) => (
                    <InputWrapper
                      label="Përjashto përdorues (opsional)"
                      error={fieldState.error?.message}
                    >
                      <AsyncSelect
                        isMulti
                        value={value}
                        onChange={(selected) => onChange(selected || [])}
                        loadOptions={loadUserOptions}
                        getOptionValue={(o: IBroadcastUserSearchResult) =>
                          o.email
                        }
                        getOptionLabel={(o: IBroadcastUserSearchResult) =>
                          o.email
                        }
                        placeholder="Shkruani të paktën 2 shkronja..."
                        noOptionsMessage={({ inputValue }) =>
                          inputValue.trim().length < 2
                            ? "Shkruani për të kërkuar"
                            : "Nuk u gjet asnjë përdorues"
                        }
                        loadingMessage={() => "Duke kërkuar..."}
                      />
                    </InputWrapper>
                  )}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Pajisjet e këtyre përdoruesve nuk e marrin njoftimin. Pajisjet
                  pa llogari të lidhur nuk mund të përjashtohen, sepse nuk kanë
                  email për t&apos;u njohur.
                </p>
                {editMode && (
                  <p className="text-xs text-warning mt-1">
                    Lista e përjashtimeve nuk ruhet me njoftimin dhe nuk shfaqet
                    këtu. Kur e ruani, dërgimi përjashton vetëm përdoruesit që
                    zgjidhni tani.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="mb-4 border-t pt-4">
          <label className="text-sm block mb-2 font-medium" htmlFor="routeChoice">
            Kur përdoruesi e prek njoftimin, hapet
          </label>
          <select
            id="routeChoice"
            {...register("routeChoice")}
            className={selectClassName}
          >
            <option value="">Vetëm aplikacioni (pa faqe të caktuar)</option>
            {appRoutes.map((route) => (
              <option key={route.path} value={route.path}>
                {route.label}
              </option>
            ))}
            <option value="custom">Faqe tjetër (shkruaje vetë)</option>
          </select>
          {routeChoice === "custom" && (
            <div className="mt-3">
              <Input
                {...register("customRoute")}
                label="Rruga e faqes (p.sh. /esim/123)"
                error={errors.customRoute?.message}
              />
            </div>
          )}
          {(routeChoice === "/games/leaderboard" ||
            routeChoice === "/games") && (
            <p className="text-xs text-gray-500 mt-1">
              Faqet e lojërave kërkojnë versionin 1.6.0 ose më të ri — mund të
              vendosni versionin minimal te filtrat më lart.
            </p>
          )}
        </div>

        <div className="border-t pt-4 mt-2">
          <h2 className="text-base font-medium mb-1">Mesazhi kryesor</h2>
          <p className="text-xs text-gray-500 mb-3">
            Ky mesazh u dërgohet të gjithë përdoruesve që nuk kanë përkthim në
            gjuhën e tyre.
          </p>
          <Input
            {...register("defaultTitle")}
            label="Titulli"
            error={errors.defaultTitle?.message}
          />
          <Input
            {...register("defaultBody")}
            label="Përmbajtja"
            error={errors.defaultBody?.message}
          />
        </div>

        <div className="border-t pt-4 mt-2">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-medium">Përkthimet (opsionale)</h2>
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-semibold text-primary"
              onClick={() =>
                append({ applicationLanguage: "", title: "", body: "" })
              }
            >
              <PlusIcon className="w-4 h-4" />
              Shto përkthim
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Përdoruesit e marrin njoftimin në gjuhën e aplikacionit të tyre nëse
            ekziston përkthimi.
          </p>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="rounded-lg border p-3 mb-3 bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="w-64">
                  <select
                    {...register(`translations.${index}.applicationLanguage`)}
                    className={selectClassName}
                  >
                    <option value="">Zgjidh gjuhën</option>
                    {appLanguages.map((lang) => (
                      <option
                        key={lang.code}
                        value={lang.code}
                        disabled={
                          usedLanguages.includes(lang.code) &&
                          usedLanguages[index] !== lang.code
                        }
                      >
                        {lang.label}
                      </option>
                    ))}
                  </select>
                  {errors.translations?.[index]?.applicationLanguage && (
                    <p className="text-xs text-warning mt-1">
                      {errors.translations[index]?.applicationLanguage?.message}
                    </p>
                  )}
                </div>
                <span title="Hiq përkthimin">
                  <IconBtn
                    className="w-6 h-6 text-neutral"
                    onClick={() => remove(index)}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </IconBtn>
                </span>
              </div>
              <Input
                {...register(`translations.${index}.title`)}
                label="Titulli"
                error={errors.translations?.[index]?.title?.message}
              />
              <Input
                {...register(`translations.${index}.body`)}
                label="Përmbajtja"
                error={errors.translations?.[index]?.body?.message}
              />
            </div>
          ))}
        </div>

        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm mb-4">
          {preview ? (
            <>
              <span className="font-semibold tabular-nums">
                {preview.totalDevices}
              </span>{" "}
              pajisje do ta marrin këtë njoftim me zgjedhjet aktuale.
            </>
          ) : audienceMode === "users" && (targetUsers || []).length === 0 ? (
            "Zgjidhni përdoruesit për të parë numrin e pajisjeve."
          ) : (
            "Duke llogaritur numrin e pajisjeve..."
          )}
        </div>
      </FormWrapper>
    </FormProvider>
  );
};

export default BroadcastForm;
